import { api, ApiError, ID, useBaseMutation, useBaseQuery } from 'api'
import { getMessageFromApiError } from 'api/shared/utils'
import { useToast } from 'components/shared/Snackbars'
import { useQueryClient } from 'react-query'
import { usePublisherStore } from 'store/publisher'
import GET_PUBLISHER from './queries/get'
import GET_ALL_PUBLISHERS from './queries/getAll'
import GET_PUBLISHER_FEES from './queries/getFees'
import UPDATE_PUBLISHER_FEES from './queries/updateFees'
import UPDATE_PUBLISHER_STATUS from './queries/updatePublisherStatus'
import { Fees, Publisher } from './types'

interface GetAllData {
  publishers: Publisher[]
}

type GetAllVariables = never

export function useGetAllPublishers() {
  return useBaseQuery<GetAllData, GetAllVariables>(
    'publishers',
    GET_ALL_PUBLISHERS,
    {}
  )
}

interface GetData {
  publisher: Publisher
}

type GetVariables = {
  id: ID
}

export function useGetPublisher(variables: GetVariables) {
  return useBaseQuery<GetData, GetVariables>(
    ['publisher', variables.id],
    GET_PUBLISHER,
    variables
  )
}

interface GetFeesData {
  queryPublisherFees: Fees
}

type GetFeesVariables = {
  id: ID
}

export function useGetPublisherFees(variables: GetFeesVariables) {
  const publisherId = usePublisherStore((state) => state.publisherId)
  return useBaseQuery<GetFeesData, GetFeesVariables>(
    ['publisherFees', publisherId],
    GET_PUBLISHER_FEES,
    variables
  )
}

interface UpdateFeesData {
  publisherUpdateFees: Fees
}

type UpdateFeesVariables = Fees

export function useUpdatePublisherFees() {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useToast()

  return useBaseMutation<UpdateFeesData, ApiError, UpdateFeesVariables>(
    (variables) =>
      api.request<UpdateFeesData, UpdateFeesVariables>(
        UPDATE_PUBLISHER_FEES,
        variables
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('publisherFees')

        showSuccess('The details are updated')
      },
      onError: (error) => showError(getMessageFromApiError(error)),
    }
  )
}

interface UpdateStatusData {
  publisher: Publisher
}

type UpdateStatusVariables = { id: ID; enabled: boolean }

export function useUpdatePublisher() {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useToast()

  return useBaseMutation<UpdateStatusData, ApiError, UpdateStatusVariables>(
    (variables) =>
      api.request<UpdateStatusData, UpdateStatusVariables>(
        UPDATE_PUBLISHER_STATUS,
        variables
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('publisher')

        showSuccess('The publisher status is updated')
      },
      onError: (error) => showError(getMessageFromApiError(error)),
    }
  )
}
